import { useId, type CSSProperties, type KeyboardEvent } from "react";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

type AiProcessingIndicatorProps = {
  title: string;
  description: string;
  detail?: string;
  progress?: number | null;
  variant?: "floating" | "panel" | "overlay";
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
};

function clampProgress(value?: number | null): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.max(0, Math.min(100, value));
}

function ProgressCircle({ value }: { value?: number | null }) {
  const gradientId = useId();
  const pct = clampProgress(value);
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const offset =
    pct == null
      ? circumference * 0.28
      : circumference - (pct / 100) * circumference;
  return (
    <div className="relative grid h-12 w-12 shrink-0 place-items-center">
      <svg
        className={pct == null ? "animate-spin" : undefined}
        viewBox="0 0 48 48"
        width="48"
        height="48"
        aria-hidden="true"
      >
        <circle
          cx="24"
          cy="24"
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,.16)"
          strokeWidth="5"
        />
        <circle
          cx="24"
          cy="24"
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 24 24)"
        />
        <defs>
          <linearGradient id={gradientId} x1="6" y1="6" x2="42" y2="42">
            <stop stopColor="#FFB400" />
            <stop offset="1" stopColor="#FF5900" />
          </linearGradient>
        </defs>
      </svg>
      {pct == null ? (
        <Sparkles className="absolute h-4 w-4 text-[#FFB47A]" />
      ) : (
        <span className="absolute text-[10px] font-bold text-[#FFE7D3]">
          {Math.round(pct)}
        </span>
      )}
    </div>
  );
}

export function AiProcessingIndicator({
  title,
  detail,
  progress,
  variant = "panel",
  className,
  style,
  onClick,
}: AiProcessingIndicatorProps) {
  const pct = clampProgress(progress);
  const compactContent = (
    <>
      <ProgressCircle value={null} />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold leading-tight text-[#F3ECE3]">
          {title}
        </div>
      </div>
    </>
  );
  const content = (
    <>
      <ProgressCircle value={pct} />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold leading-tight text-[#F3ECE3]">
          {title}
        </div>
        {detail && (
          <div className="mt-1 truncate text-[11px] leading-tight text-[#C0B2A4]">
            {detail}
          </div>
        )}
        {pct != null && (
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-[linear-gradient(90deg,#FFB400,#FF5900)] transition-[width]"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>
    </>
  );

  if (variant === "floating") {
    const clickableProps = onClick
      ? {
          role: "button" as const,
          tabIndex: 0,
          onClick,
          onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick();
            }
          },
        }
      : {
          role: "status" as const,
        };

    return (
      <div
        aria-live="polite"
        aria-label={title}
        {...clickableProps}
        className={cn(
          "fixed bottom-5 right-5 z-50 flex w-[min(calc(100vw-32px),360px)] items-center gap-3 rounded-2xl border border-white/15 bg-[#16100c]/95 p-3 shadow-[0_18px_50px_rgba(0,0,0,.45)] backdrop-blur",
          onClick && "cursor-pointer outline-none transition hover:border-white/25 focus-visible:ring-2 focus-visible:ring-[#FF8A4B]",
          className,
        )}
        style={style}
      >
        {pct == null ? compactContent : content}
      </div>
    );
  }

  if (variant === "overlay") {
    return (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "absolute inset-0 z-30 flex items-center justify-center bg-black/50 p-5 backdrop-blur-sm",
          className,
        )}
        style={style}
      >
        <div className="flex w-[min(100%,390px)] items-center gap-4 rounded-2xl border border-white/15 bg-[#16100c]/95 p-4 shadow-[0_18px_50px_rgba(0,0,0,.5)]">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-center gap-4 rounded-2xl border border-white/15 bg-[linear-gradient(160deg,rgba(30,25,21,.92),rgba(16,13,11,.88))] p-4 shadow-[0_18px_46px_rgba(0,0,0,.38)]",
        className,
      )}
      style={style}
    >
      {content}
    </div>
  );
}
